/* eslint-disable indent */
/*
  Lightweight HTTP instrumentation for axios and fetch.
  - Captures start/end timestamps and duration for every request
  - Works for both success and error responses (including cancellations)
  - Stores logs on window.__apiPerf for QA to inspect/export
  - Toggle via localStorage key `apiPerfLogging` ("true"/"false") or by calling setup directly
*/

import axios from 'axios';

// Ensure a single global store for logs and utilities
function initGlobalStore() {
  if (!window.__apiPerf) {
    const store = {
      logs: [],
      maxLogs: 1000,
      listeners: new Set(),
      add(log) {
        // Keep only the last maxLogs entries
        this.logs.push(log);
        if (this.logs.length > this.maxLogs) {
          this.logs.splice(0, this.logs.length - this.maxLogs);
        }
        // Notify listeners
        this.listeners.forEach((fn) => {
          try { fn(log); } catch (_) { /* noop */ }
        });
      },
      clear() { this.logs = []; },
      onLog(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
      exportToCSV() {
        const headers = [
          'id','transport','method','url','status','ok','startTs','endTs','durationMs','error','cancelled','requestId','traceId'
        ];
        const rows = this.logs.map((l) => [
          l.id,
          l.transport,
          l.method,
          l.url,
          (l.status != null ? l.status : ''),
          l.ok,
          new Date(l.startTs).toISOString(),
          new Date(l.endTs).toISOString(),
          l.durationMs.toFixed(1),
          (l.error && (l.error.code || l.error.message)) || '',
          l.cancelled || false,
          l.requestId || '',
          l.traceId || '',
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.map(x => typeof x === 'string' && x.includes(',') ? `"${x.replace(/"/g,'""')}"` : x).join(','))].join('\n');
        return csv;
      }
    };
    Object.defineProperty(window, '__apiPerf', { value: store, configurable: false, enumerable: false, writable: false });
  }
  return window.__apiPerf;
}

let axiosReqInterceptorId = null;
let axiosResInterceptorId = null;
let fetchInstrumented = false;
let nextId = 1;

function nowMs() { return Date.now(); }
function nowPerf() { return (window.performance && performance.now) ? performance.now() : null; }

function pickHeader(headers, name) {
  if (!headers) return undefined;
  const key = Object.keys(headers).find(k => k.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : undefined;
}

export function setupHttpInstrumentation(options = {}) {
  const store = initGlobalStore();

  const {
    enable = (typeof localStorage !== 'undefined' && localStorage.getItem('apiPerfLogging') === 'true'),
    logToConsole = true,
    maxLogs = 1000,
    axiosInstance = axios, // allow passing a dedicated instance
  } = options;

  if (!enable) {
    return { enabled: false };
  }

  store.maxLogs = maxLogs;

  // Axios interceptors (instance-level if provided)
  if (axiosReqInterceptorId == null) {
    axiosReqInterceptorId = axiosInstance.interceptors.request.use((config) => {
      // attach metadata
      const id = nextId++;
      config.__perf = {
        id,
        startTs: nowMs(),
        startPerf: nowPerf(),
      };
      return config;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  if (axiosResInterceptorId == null) {
    axiosResInterceptorId = axiosInstance.interceptors.response.use((response) => {
      try {
        const meta = response.config.__perf || {};
        const endTs = nowMs();
        const durationMs = meta.startPerf != null && nowPerf() != null
          ? (nowPerf() - meta.startPerf)
          : (endTs - (meta.startTs || endTs));
        const requestId = response.headers && (response.headers['x-request-id'] || response.headers['x-correlation-id']);
        const traceId = response.headers && (response.headers['x-trace-id'] || response.headers['traceparent']);
        const startTsVal = meta.startTs || endTs;
        const log = {
          id: meta.id || nextId++,
          transport: 'axios',
          method: (response.config.method || 'GET').toUpperCase(),
          url: response.config.url,
          status: response.status,
          ok: true,
          startTs: startTsVal,
          endTs,
          startIso: new Date(startTsVal).toISOString(),
          endIso: new Date(endTs).toISOString(),
          durationMs,
          error: null,
          cancelled: false,
          requestId,
          traceId,
        };
        store.add(log);
        if (logToConsole) {
          // eslint-disable-next-line no-console
          console.debug('[API PERF]', `${log.method} ${log.url}`, `${log.status}`, `${log.durationMs.toFixed(1)}ms`, log);
        }
      } catch (_) { /* noop */ }
      return response;
    }, (error) => {
      try {
        const cfg = error.config || {};
        const meta = cfg.__perf || {};
        const endTs = nowMs();
        const durationMs = meta.startPerf != null && nowPerf() != null
          ? (nowPerf() - meta.startPerf)
          : (endTs - (meta.startTs || endTs));
        const cancelled = axios.isCancel ? axios.isCancel(error) : (/cancel/i.test(String(error && error.message)));
        const headers = (error.response && error.response.headers) || {};
        const requestId = pickHeader(headers, 'x-request-id') || pickHeader(headers, 'x-correlation-id');
        const traceId = pickHeader(headers, 'x-trace-id') || pickHeader(headers, 'traceparent');
        const startTsVal = meta.startTs || endTs;
        const log = {
          id: meta.id || nextId++,
          transport: 'axios',
          method: (cfg.method || 'GET').toUpperCase(),
          url: cfg.url,
          status: (error.response && error.response.status) || null,
          ok: false,
          startTs: startTsVal,
          endTs,
          startIso: new Date(startTsVal).toISOString(),
          endIso: new Date(endTs).toISOString(),
          durationMs,
          error: { message: error.message, code: error.code },
          cancelled,
          requestId,
          traceId,
        };
        store.add(log);
        if (logToConsole) {
          // eslint-disable-next-line no-console
          console.warn('[API PERF]', `${log.method} ${log.url}`, `${log.status || 'ERR'}`, `${log.durationMs.toFixed(1)}ms`, log);
        }
      } catch (_) { /* noop */ }
      return Promise.reject(error);
    });
  }

  // fetch wrapper
  if (typeof window !== 'undefined' && window.fetch && !fetchInstrumented) {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
      const id = nextId++;
      const method = (init && init.method ? String(init.method) : 'GET').toUpperCase();
      const url = (typeof input === 'string') ? input : (input && input.url) || String(input);
      const startTs = nowMs();
      const startPerf = nowPerf();
      try {
        const res = await originalFetch(input, init);
        const endTs = nowMs();
        const durationMs = startPerf != null && nowPerf() != null ? (nowPerf() - startPerf) : (endTs - startTs);
        // Headers for fetch are an iterable; try to pick common IDs
        let requestId, traceId;
        try {
          requestId = res.headers.get('x-request-id') || res.headers.get('x-correlation-id');
          traceId = res.headers.get('x-trace-id') || res.headers.get('traceparent');
        } catch (_) { /* noop */ }
        const log = {
          id,
          transport: 'fetch',
          method,
          url,
          status: res.status,
          ok: res.ok,
          startTs,
          endTs,
          startIso: new Date(startTs).toISOString(),
          endIso: new Date(endTs).toISOString(),
          durationMs,
          error: null,
          cancelled: false,
          requestId,
          traceId,
        };
        store.add(log);
        if (logToConsole) {
          // eslint-disable-next-line no-console
          console.debug('[API PERF]', `${method} ${url}`, `${res.status}`, `${log.durationMs.toFixed(1)}ms`, log);
        }
        return res;
      } catch (error) {
        const endTs = nowMs();
        const durationMs = startPerf != null && nowPerf() != null ? (nowPerf() - startPerf) : (endTs - startTs);
        const log = {
          id,
          transport: 'fetch',
          method,
          url,
          status: null,
          ok: false,
          startTs,
          endTs,
          startIso: new Date(startTs).toISOString(),
          endIso: new Date(endTs).toISOString(),
          durationMs,
          error: { message: error.message },
          cancelled: false,
          requestId: undefined,
          traceId: undefined,
        };
        store.add(log);
        if (logToConsole) {
          // eslint-disable-next-line no-console
          console.warn('[API PERF]', `${method} ${url}`, 'ERR', `${log.durationMs.toFixed(1)}ms`, log);
        }
        throw error;
      }
    };
    fetchInstrumented = true;
  }

  return { enabled: true, store: window.__apiPerf };
}

// Auto-setup if toggle is in localStorage
// Consumers may also import and call setupHttpInstrumentation manually with options
try {
  const auto = (typeof localStorage !== 'undefined' && localStorage.getItem('apiPerfLogging') === 'true');
  if (auto) {
    setupHttpInstrumentation();
  }
} catch (_) { /* noop */ }

export default setupHttpInstrumentation;



// WEBPACK FOOTER //
// src/commons/httpInstrumentation.js